import { actionStateLabels } from "./types.ts";
import type { CSSProperties } from "react";
import type { AutomationSummary } from "../../../../shared/triggers/report.ts";
export function automationStyle(
  automation: AutomationSummary | undefined,
): CSSProperties {
  const color = automation?.highlight?.color;
  return color && /^#[0-9a-fA-F]{6}$/.test(color)
    ? ({
        "--trigger-row-color": color,
        "--trigger-row-tint": color + "18",
      } as CSSProperties)
    : {};
}
export function TriggerBadge({
  automation,
  onDetails,
}: {
  automation?: AutomationSummary;
  onDetails: (id: string) => void;
}) {
  if (!automation) return null;
  const confirmed =
    automation.confirmedDisable ?? automation.parentConfirmedDisable;
  if (confirmed)
    return (
      <span className="trigger-report-decoration">
        <button
          type="button"
          className="trigger-badge"
          onClick={(e) => {
            e.stopPropagation();
            onDetails(confirmed.matchId);
          }}
          aria-label={"Чому спрацювало " + confirmed.name + "?"}
        >
          {automation.confirmedDisable
            ? "Вимкнено тригером"
            : "Кампанію вимкнено тригером"}{" "}
          · {confirmed.name}
        </button>
        <small>
          {new Date(confirmed.disabledAt).toLocaleString("uk-UA")}
          {automation.parentConfirmedDisable
            ? " · Статус оголошення залежить від кампанії"
            : ""}
        </small>
      </span>
    );
  if (!automation.decisions.length) {
    const task = automation.outcomes?.[0],
      counts = automation.campaignOutcomes;
    if (!task && !counts?.confirmed) return null;
    return (
      <span className="trigger-report-decoration">
        {task && (
          <button
            type="button"
            className="trigger-badge"
            onClick={(e) => {
              e.stopPropagation();
              onDetails(task.matchId);
            }}
          >
            {counts
              ? "Результати кампаній"
              : (actionStateLabels[task.state] ?? task.state)}
          </button>
        )}
        {counts && (
          <small>
            Кампаній: {counts.confirmed} підтверджено · {counts.queued} очікують
            · {counts.failed} помилок · {counts.uncertain} з невідомим
            авторством
          </small>
        )}
      </span>
    );
  }
  const primary =
    automation.decisions.find(
      (d) => d.triggerId === automation.highlight?.triggerId,
    ) ?? automation.decisions[0];
  const text =
    primary.relation === "parent_campaign"
      ? "Збіг для кампанії"
      : primary.relation === "creative_target"
        ? "Ціль правила крео"
        : primary.relation === "child_campaign"
          ? automation.childCampaignMatches + " кампаній зі збігом"
          : "Збіг правила";
  const extra = new Set(automation.decisions.map((d) => d.triggerId)).size - 1;
  return (
    <span className="trigger-report-decoration">
      <button
        type="button"
        className="trigger-badge"
        disabled={!primary.matchId}
        onClick={(e) => {
          e.stopPropagation();
          if (primary.matchId) onDetails(primary.matchId);
        }}
        aria-label={
          "Чому спрацювало " +
          primary.name +
          "? " +
          text +
          ". Період тригера: сьогодні."
        }
      >
        <span aria-hidden="true" className="trigger-badge-dot" />
        <span>
          {text} · {primary.name}
          {extra > 0 ? " +" + extra : ""}
        </span>
        <span className="trigger-badge-mode">
          {automation.mode === "dry_run"
            ? "dry-run"
            : actionStateLabels[automation.outcomes?.[0]?.state ?? "queued"]}
        </span>
        {(primary.stale || automation.highlight?.stale) && (
          <span>· застарілі дані</span>
        )}
      </button>
      <small>
        За періодом тригера: сьогодні
        {automation.parentPaused
          ? " · Кампанія неактивна; авторство не підтверджено"
          : ""}
      </small>
      {automation.decisions.length > 1 && (
        <details>
          <summary>Усі правила ({automation.decisions.length})</summary>
          {automation.decisions.map((d) => (
            <button
              type="button"
              className="trigger-badge"
              key={d.contextKey}
              disabled={!d.matchId}
              onClick={(e) => {
                e.stopPropagation();
                if (d.matchId) onDetails(d.matchId);
              }}
            >
              {d.name} ·{" "}
              {d.relation === "subject"
                ? "збіг"
                : d.relation === "parent_campaign"
                  ? "батьківська кампанія"
                  : "пов’язаний об’єкт"}{" "}
              · {d.targets.length} цілей
            </button>
          ))}
        </details>
      )}
    </span>
  );
}
